import React, { useState, useRef } from 'react';
import { newRecordId } from '../services/api';
import {
  Clipboard as ClipboardIcon,
  Plus,
  X,
  Edit2,
  Check,
  Copy,
  Maximize2,
  Trash2,
  CheckSquare,
  Square,
  GripVertical,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { renderRichText } from '../utils/richText';

const HINT =
  "Tip: start a line with '- ' for a bullet, '1. ' for a numbered list, or '[] ' for a checkbox. Links become clickable.";

// Classify a single content line into a renderable kind.
const parseLine = (line) => {
  const todo = line.match(/^\[(x|X| )?\]\s?(.*)$/);
  if (todo) {
    return { type: 'todo', checked: (todo[1] || '').toLowerCase() === 'x', text: todo[2] };
  }
  const bullet = line.match(/^[-*]\s+(.*)$/);
  if (bullet) return { type: 'bullet', text: bullet[1] };
  const number = line.match(/^(\d+)\.\s+(.*)$/);
  if (number) return { type: 'number', number: number[1], text: number[2] };
  return { type: 'text', text: line };
};

const countTodos = (content) => {
  const lines = (content || '').split('\n');
  let total = 0;
  let done = 0;
  for (const line of lines) {
    const p = parseLine(line);
    if (p.type === 'todo') {
      total += 1;
      if (p.checked) done += 1;
    }
  }
  return { total, done };
};

const Clipboard = ({
  cards,
  onAddCard,
  onUpdateCard,
  onDeleteCard,
  onDeleteAllCards,
  onReorderCards,
  onMovePanelUp,
  onMovePanelDown,
  isFirstPanel,
  isLastPanel,
}) => {
  const [newCardTitle, setNewCardTitle] = useState('');
  const [newCardContent, setNewCardContent] = useState('');
  const [showAddCard, setShowAddCard] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingContent, setEditingContent] = useState('');
  const [cardHeights, setCardHeights] = useState({});
  const [draggedCardId, setDraggedCardId] = useState(null);
  const [dragOverCardId, setDragOverCardId] = useState(null);
  const gridRef = useRef(null);

  const handleCardDragStart = (e, cardId) => {
    setDraggedCardId(cardId);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', String(cardId));
    } catch (_) {}
  };

  const handleCardDragEnd = () => {
    setDraggedCardId(null);
    setDragOverCardId(null);
  };

  const handleCardDragOver = (e, cardId) => {
    if (draggedCardId == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (cardId !== dragOverCardId) setDragOverCardId(cardId);
  };

  const handleCardDrop = (e, targetCardId) => {
    if (draggedCardId == null) return;
    e.preventDefault();
    if (draggedCardId !== targetCardId && onReorderCards) {
      onReorderCards(draggedCardId, targetCardId);
    }
    setDraggedCardId(null);
    setDragOverCardId(null);
  };

  const handleAddCard = () => {
    if (!newCardTitle.trim()) return;

    onAddCard({
      id: newRecordId(),
      title: newCardTitle.trim(),
      content: newCardContent,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    setNewCardTitle('');
    setNewCardContent('');
    setShowAddCard(false);
  };

  const startEditing = (card) => {
    setEditingId(card.id);
    setEditingTitle(card.title);
    setEditingContent(card.content || '');
  };

  const saveEdit = () => {
    if (!editingTitle.trim()) return;

    onUpdateCard(editingId, {
      title: editingTitle.trim(),
      content: editingContent,
      // Drop any legacy structured blocks now that content is the source of truth.
      blocks: undefined,
      updatedAt: new Date().toISOString(),
    });

    setEditingId(null);
    setEditingTitle('');
    setEditingContent('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingTitle('');
    setEditingContent('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Toggle a checkbox line straight from view mode by rewriting that line.
  const toggleTodoLine = (card, lineIndex) => {
    const lines = (card.content || '').split('\n');
    const line = lines[lineIndex];
    const m = line.match(/^\[(x|X| )?\]\s?(.*)$/);
    if (!m) return;
    const checked = (m[1] || '').toLowerCase() === 'x';
    lines[lineIndex] = `[${checked ? ' ' : 'x'}] ${m[2]}`;
    onUpdateCard(card.id, {
      content: lines.join('\n'),
      blocks: undefined,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleResize = (cardId, newHeight) => {
    setCardHeights((prev) => ({
      ...prev,
      [cardId]: newHeight,
    }));
  };

  const renderContent = (card) => {
    const content = card.content || '';
    if (!content.trim()) {
      return <span className="text-gray-500 italic text-sm">No content</span>;
    }
    const lines = content.split('\n');
    return (
      <div className="space-y-1">
        {lines.map((line, i) => {
          const p = parseLine(line);
          const key = `${card.id}-${i}`;

          if (p.type === 'todo') {
            return (
              <button
                key={key}
                onClick={() => toggleTodoLine(card, i)}
                className="flex gap-2 text-sm text-left w-full"
                title="Toggle done"
              >
                {p.checked ? (
                  <CheckSquare className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <Square className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                )}
                <span
                  className={`whitespace-pre-wrap break-words ${
                    p.checked ? 'text-gray-500 line-through' : 'text-gray-300'
                  }`}
                >
                  {renderRichText(p.text, key)}
                </span>
              </button>
            );
          }
          if (p.type === 'bullet') {
            return (
              <div key={key} className="flex gap-2 text-sm text-gray-300">
                <span className="text-cyan-400 flex-shrink-0">•</span>
                <span className="whitespace-pre-wrap break-words">{renderRichText(p.text, key)}</span>
              </div>
            );
          }
          if (p.type === 'number') {
            return (
              <div key={key} className="flex gap-2 text-sm text-gray-300">
                <span className="text-cyan-400 flex-shrink-0">{p.number}.</span>
                <span className="whitespace-pre-wrap break-words">{renderRichText(p.text, key)}</span>
              </div>
            );
          }
          if (!p.text) {
            return <div key={key} className="h-2" />;
          }
          return (
            <p key={key} className="text-gray-300 text-sm whitespace-pre-wrap break-words">
              {renderRichText(p.text, key)}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-gray-800 rounded-3xl p-4 sm:p-6 border-2 border-cyan-500/30 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardIcon className="w-5 h-5 text-cyan-400 flex-shrink-0" />
          <h3 className="text-xl font-bold text-cyan-400">Clipboard</h3>
          <span className="text-xs text-gray-500">({cards.length})</span>
        </div>
        <div className="flex items-center gap-2">
          {(onMovePanelUp || onMovePanelDown) && (
            <div className="flex items-center gap-1">
              <button
                onClick={onMovePanelUp}
                disabled={isFirstPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel up"
              >
                <ChevronUp className="w-4 h-4 text-cyan-300" />
              </button>
              <button
                onClick={onMovePanelDown}
                disabled={isLastPanel}
                className="p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Move panel down"
              >
                <ChevronDown className="w-4 h-4 text-cyan-300" />
              </button>
            </div>
          )}
          {cards.length > 0 && onDeleteAllCards && (
            <button
              onClick={onDeleteAllCards}
              className="p-2 bg-red-500/20 hover:bg-red-500/40 border border-red-500/50 rounded-xl transition-colors"
              title="Delete all cards"
            >
              <Trash2 className="w-5 h-5 text-red-400" />
            </button>
          )}
          <button
            onClick={() => setShowAddCard(!showAddCard)}
            className="p-2 bg-cyan-500 hover:bg-cyan-600 rounded-xl transition-colors"
            title="Add new card"
          >
            {showAddCard ? <X className="w-5 h-5 text-white" /> : <Plus className="w-5 h-5 text-white" />}
          </button>
        </div>
      </div>

      {/* Add New Card Form */}
      {showAddCard && (
        <div className="mb-4 p-4 bg-gray-700/50 rounded-xl border-2 border-cyan-500/50">
          <input
            type="text"
            value={newCardTitle}
            onChange={(e) => setNewCardTitle(e.target.value)}
            placeholder="Card title..."
            className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-cyan-500 outline-none mb-2"
            autoFocus
          />
          <textarea
            value={newCardContent}
            onChange={(e) => setNewCardContent(e.target.value)}
            placeholder="Paste or type content here..."
            rows="5"
            className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-lg border-2 border-gray-600 focus:border-cyan-500 outline-none resize-y mb-1"
          />
          <p className="text-xs text-gray-400 mb-2">{HINT}</p>
          <div className="flex gap-2">
            <button
              onClick={handleAddCard}
              disabled={!newCardTitle.trim()}
              className="flex-1 bg-cyan-500 hover:bg-cyan-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Add Card
            </button>
            <button
              onClick={() => {
                setShowAddCard(false);
                setNewCardTitle('');
                setNewCardContent('');
              }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cards Grid */}
      <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.length === 0 && !showAddCard && (
          <div className="col-span-full text-center py-8 text-gray-400">
            <p>No cards yet. Click the + button to add your first card!</p>
          </div>
        )}

        {cards.map((card) => {
          const customHeight = cardHeights[card.id];
          const { total, done } = countTodos(card.content);

          return (
            <div
              key={card.id}
              data-card-id={card.id}
              onDragOver={(e) => handleCardDragOver(e, card.id)}
              onDrop={(e) => handleCardDrop(e, card.id)}
              className={`bg-gray-700/50 rounded-xl p-4 border-2 transition-all flex flex-col relative group overflow-hidden ${
                dragOverCardId === card.id && draggedCardId !== card.id
                  ? 'border-cyan-400 ring-2 ring-cyan-400/50'
                  : 'border-gray-600 hover:border-cyan-500/50'
              } ${draggedCardId === card.id ? 'opacity-50' : ''}`}
              style={{
                height: customHeight ? `${customHeight}px` : 'auto',
              }}
            >
              {editingId === card.id ? (
                // Edit Mode
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <input
                    type="text"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    className="w-full bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border-2 border-cyan-500 outline-none mb-2 font-semibold flex-shrink-0"
                  />
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    className="flex-1 w-full bg-gray-600 text-cyan-200 px-2 py-1 rounded-lg border-2 border-cyan-500 outline-none resize-none mb-1 text-sm min-h-0"
                    style={{ minHeight: '100px' }}
                  />
                  <p className="text-[11px] text-gray-400 mb-2 flex-shrink-0">{HINT}</p>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={saveEdit}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-colors"
                      title="Save"
                    >
                      <Check className="w-4 h-4 mx-auto" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex-1 bg-gray-600 hover:bg-gray-500 text-white p-2 rounded-lg transition-colors"
                      title="Cancel"
                    >
                      <X className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                  <div className="flex items-start justify-between mb-2 flex-shrink-0">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-cyan-300 font-semibold text-lg break-words">
                        {card.title}
                      </h4>
                      {total > 0 && (
                        <span className="text-xs text-gray-400">
                          {done}/{total} done
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2 flex-shrink-0">
                      <span
                        draggable
                        onDragStart={(e) => handleCardDragStart(e, card.id)}
                        onDragEnd={handleCardDragEnd}
                        className="text-gray-500 hover:text-cyan-400 transition-colors p-1 cursor-grab active:cursor-grabbing"
                        title="Drag to reorder card"
                      >
                        <GripVertical className="w-4 h-4" />
                      </span>
                      <button
                        onClick={() => copyToClipboard(card.content)}
                        className="text-gray-400 hover:text-cyan-400 transition-colors p-1"
                        title="Copy content"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => startEditing(card)}
                        className="text-gray-400 hover:text-yellow-400 transition-colors p-1"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete card "${card.title}"?`)) {
                            onDeleteCard(card.id);
                          }
                        }}
                        className="text-gray-400 hover:text-red-400 transition-colors p-1"
                        title="Delete"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div
                    className="flex-1 overflow-y-auto min-h-0"
                    style={{ maxHeight: customHeight ? 'none' : '160px' }}
                  >
                    {renderContent(card)}
                  </div>
                  <p className="text-xs text-gray-500 mt-2 flex-shrink-0">
                    {new Date(card.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              )}

              {/* Resize Handle - Bottom Right Corner */}
              {editingId !== card.id && (
                <div
                  className="absolute bottom-1 right-1 cursor-ns-resize opacity-0 group-hover:opacity-100 transition-opacity z-10 p-1 bg-gray-800/80 rounded"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startY = e.clientY;
                    const cardElement = e.currentTarget.parentElement;
                    const startHeight = customHeight || cardElement.offsetHeight;

                    const handleMouseMove = (moveEvent) => {
                      const deltaY = moveEvent.clientY - startY;
                      const newHeight = Math.max(200, startHeight + deltaY);
                      handleResize(card.id, newHeight);
                    };

                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };

                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                  title="Drag to resize height"
                >
                  <Maximize2 className="w-4 h-4 text-gray-400 hover:text-cyan-400" />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Clipboard;

